import React, { useRef, useState } from 'react';
import { ArrowLeft, ArrowLeftRight, MoreVertical, Sparkles, Wrench } from 'lucide-react';
import { Finding, FindingStatus } from '../../types/finding';
import { Spinner } from '../../design-system';
import { FINDING_STATUS_LABEL } from '../../design-system/severity';
import PopoverPortal from '../common/PopoverPortal';

interface FindingRowActionsProps {
  finding: Finding;
  /** Disabled (with a reason tooltip) for viewers and demo workspaces. */
  canWrite: boolean;
  /** True while this row's action is in flight. */
  isBusy: boolean;
  onProposeFix: (finding: Finding) => void;
  onViewFix: (finding: Finding) => void;
  onUpdateStatus: (finding: Finding, status: FindingStatus) => void;
}

const STATUS_OPTIONS: FindingStatus[] = [
  'open',
  'resolved',
  'ignored',
  'false_positive',
  'needs_manual_review',
];

/** A finding can only get a new fix while it is still actionable. */
const canProposeFix = (finding: Finding): boolean =>
  finding.status === 'open' || finding.status === 'needs_manual_review';

const MENU_ITEM_CLASS =
  'w-full flex items-center gap-2 px-3 py-[7px] text-left text-[length:var(--fs-sm)] text-[color:var(--text-body)] hover:bg-[var(--surface-2)] hover:text-[color:var(--text-primary)] transition-colors disabled:opacity-45 disabled:cursor-not-allowed disabled:hover:bg-transparent';

const EYEBROW_CLASS =
  'px-3 pt-1.5 pb-1 text-[length:var(--fs-eyebrow)] font-semibold uppercase tracking-[var(--tracking-eyebrow)] text-[color:var(--text-faint)]';

/**
 * Per-row overflow menu on the findings table: propose (or view) a fix and change the
 * finding's triage status without leaving the list.
 */
const FindingRowActions: React.FC<FindingRowActionsProps> = ({
  finding,
  canWrite,
  isBusy,
  onProposeFix,
  onViewFix,
  onUpdateStatus,
}) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<'actions' | 'status'>('actions');

  const close = () => {
    setOpen(false);
    setView('actions');
  };

  const hasFix = !!finding.fix_summary;
  const disabledReason = canWrite ? undefined : 'You need write access to change findings';

  return (
    // z-10 keeps the trigger above the row-wide link overlay.
    <div ref={anchorRef} className="relative z-10 flex justify-end">
      <button
        type="button"
        aria-label="Finding actions"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(event) => {
          event.stopPropagation();
          if (open) close();
          else setOpen(true);
        }}
        disabled={isBusy}
        className={`flex h-7 w-7 items-center justify-center rounded-[var(--radius-xs)] border bg-[var(--surface-2)] transition-colors ${
          open
            ? 'border-[color:var(--border-strong)] text-[color:var(--text-primary)]'
            : 'border-[color:var(--border-subtle)] text-[color:var(--text-muted)] hover:border-[color:var(--border)] hover:text-[color:var(--text-primary)]'
        }`}
      >
        {isBusy ? <Spinner size={14} tone="current" /> : <MoreVertical className="h-4 w-4" />}
      </button>

      <PopoverPortal
        anchorRef={anchorRef}
        open={open}
        onClose={close}
        align="end"
        width={190}
        gap={6}
        className="rounded-[var(--radius-md)] border border-[color:var(--border)] bg-[var(--bg-overlay)] py-1.5 shadow-[var(--shadow-menu)] overflow-hidden"
      >
        {view === 'actions' ? (
          <div role="menu">
            <div className={EYEBROW_CLASS}>Actions</div>
            {hasFix ? (
              <button
                type="button"
                role="menuitem"
                className={MENU_ITEM_CLASS}
                onClick={() => {
                  close();
                  onViewFix(finding);
                }}
              >
                <Wrench className="h-3.5 w-3.5 text-[color:var(--accent-ink)]" />
                View fix
              </button>
            ) : (
              <button
                type="button"
                role="menuitem"
                className={MENU_ITEM_CLASS}
                disabled={!canWrite || !canProposeFix(finding)}
                title={disabledReason ?? (canProposeFix(finding) ? undefined : 'Only open findings can be fixed')}
                onClick={() => {
                  close();
                  onProposeFix(finding);
                }}
              >
                <Sparkles className="h-3.5 w-3.5 text-[color:var(--accent-ink)]" />
                Propose fix
              </button>
            )}
            <button
              type="button"
              role="menuitem"
              className={MENU_ITEM_CLASS}
              disabled={!canWrite}
              title={disabledReason}
              onClick={() => setView('status')}
            >
              <ArrowLeftRight className="h-3.5 w-3.5 text-[color:var(--text-muted)]" />
              Update status
            </button>
          </div>
        ) : (
          <div role="menu">
            <button
              type="button"
              className={`${EYEBROW_CLASS} flex w-full items-center gap-1.5 hover:text-[color:var(--text-secondary)]`}
              onClick={() => setView('actions')}
            >
              <ArrowLeft className="h-3 w-3" />
              Set status
            </button>
            {STATUS_OPTIONS.map((status) => (
              <button
                key={status}
                type="button"
                role="menuitem"
                className={MENU_ITEM_CLASS}
                disabled={finding.status === status}
                onClick={() => {
                  close();
                  onUpdateStatus(finding, status);
                }}
              >
                {FINDING_STATUS_LABEL[status]}
              </button>
            ))}
          </div>
        )}
      </PopoverPortal>
    </div>
  );
};

export default FindingRowActions;
